import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
  Link,
  Alert,
  Stack,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setRole } from "../Reducers/role";
import { AWS_LINK, DEV_LINK } from "../Static/static";

// Validation Schema
const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email format").required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loginType, setLoginType] = useState("ROOT");
  const [error, setError] = useState({ open: false, title: "", message: "" });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    const payload = { ...values, type: loginType };
    const LINK = process.env.NODE_ENV === "production" ? AWS_LINK : DEV_LINK;

    try {
      const response = await axios.post(`${LINK}/login`, payload, {
        withCredentials: true,
      });

      if (response.status === 200) {
        dispatch(
          setRole({
            role: loginType,
            id: response.data.user.id,
            permissions: response.data.user.permissions,
            organization_id: response.data.user.organization_id,
            email: response.data.user.email,
            token: response.data.token,
            tutor_locations: response.data.tutor_locations,
            tutor_programs: response.data.tutor_programs,
            first_name: response.data.user.first_name,
            last_name: response.data.user.last_name,
          })
        );

        sessionStorage.setItem("token", response.data.token);
        localStorage.setItem("theme", "light");
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data || "Unable to sign in. Please try again.";
      setError({ open: true, title: "Login Failed", message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const roleLabels = {
    ROOT: "Root / Super Admin",
    ADMIN: "Staff / Admin",
    TUTOR: "Tutor",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            bgcolor: "primary.main",
          },
        }}
      >
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h4"
            fontWeight={700}
            color="primary.main"
            gutterBottom
          >
            Welcome Back
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign in to continue
          </Typography>
        </Box>

        {/* Role Selector – big, clear, impossible to miss */}
        <Box mb={4}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            gutterBottom
            align="center"
          >
            I am logging in as
          </Typography>

          <ToggleButtonGroup
            value={loginType}
            exclusive
            onChange={(_, newType) => newType && setLoginType(newType)}
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                py: 1.8,
                fontSize: "1.05rem",
                fontWeight: 500,
                borderRadius: 2,
                textTransform: "none",
              },
              "& .Mui-selected": {
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": { bgcolor: "primary.dark" },
              },
            }}
          >
            
            <ToggleButton value="ROOT">Root</ToggleButton>
            <ToggleButton value="ADMIN">Staff</ToggleButton>
            <ToggleButton value="TUTOR">Tutor</ToggleButton>
          </ToggleButtonGroup>

          <Typography
            variant="body2"
            color="primary.main"
            align="center"
            mt={1.5}
            fontWeight={500}
          >
            Logging in as <strong>{roleLabels[loginType]}</strong>
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {/* Error */}
        {error.open && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError({ open: false, title: "", message: "" })}
          >
            <strong>{error.title}</strong> — {error.message}
          </Alert>
        )}

        {/* Form */}
        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={LoginSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form noValidate>
              <Field
                as={TextField}
                name="email"
                label="Email address"
                fullWidth
                autoFocus
                autoComplete="email"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                margin="normal"
                variant="outlined"
                sx={{ mb: 3 }}
              />

              <Field
                as={TextField}
                name="password"
                label="Password"
                type="password"
                fullWidth
                autoComplete="current-password"
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                margin="normal"
                variant="outlined"
                sx={{ mb: 4 }}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.8,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: 2,
                }}
              >
                {isSubmitting ? "Signing in..." : "Sign In"}
              </Button>
            </Form>
          )}
        </Formik>

        {/* Links */}
        <Box mt={4} textAlign="center">
          <Link
            href="/forgot_password"
            underline="hover"
            variant="body2"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            Forgot your password?
          </Link>

          <Link
            href="/register"
            underline="hover"
            variant="body2"
            color="text.secondary"
          >
            Don't have an account? Create one
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}